import React, { useMemo } from 'react';
import BibliothequePlotly from '../BibliothequePlotly/BibliothequePlotly';
import './styles/index.scss';
import * as glossaire_type_de_graphe from '../../../../ressources/glossaires/glossaire_type_de_graphe';
import {
  LabelHoverCard, LabelEditDialog, PhaseEditDialog,
} from './LabelComponents';
import { TOOLBAR_H } from './utils/labelConstants';
import useLabelCrud from './hooks/useLabelCrud';
import usePhaseCrud from './hooks/usePhaseCrud';
import usePlotlyData from './hooks/usePlotlyData';
import usePlotlyEvents from './hooks/usePlotlyEvents';
import LabelToolbar from './components/LabelToolbar';
import SignalHoverPopup from './components/SignalHoverPopup';

const LabelEventGraph = ({
  donnees = [],
  configuration = {},
  donneesGraphe,
  dimensionWidth,
  dimensionHeight,
  switchAfficherModifierIntervalle,
  viewobj_json_content = { displayModeBar: true },
  onLabelHover,
  onLabelUnhover,
  onPhaseClick,
  onAddLabel,
}) => {
  const isLabelMode =
    donneesGraphe?.config?.graph_type_id === glossaire_type_de_graphe.graphe_x_t_label;

  const graphId = donneesGraphe?.config?.graph_id;
  // In non-label mode, pass null to prevent API calls inside the CRUD hooks.
  const labelGraphId = isLabelMode ? graphId : null;

  const graphCurves = useMemo(() => {
    if (donneesGraphe?.graphCurves?.length) return donneesGraphe.graphCurves;
    return donnees
      .filter((d) => d.id_sensor && d.position_in_sensor !== undefined)
      .map((d) => ({ formula: `#${d.id_sensor},${d.position_in_sensor}#` }));
  }, [donneesGraphe?.graphCurves, donnees]);

  const sensorIds = useMemo(() => {
    if (donneesGraphe?.id_sensor?.length) return donneesGraphe.id_sensor;
    const fromDonnees = [...new Set(donnees.map((d) => d.id_sensor).filter(Boolean))];
    if (fromDonnees.length) return fromDonnees;
    return [...new Set(
      (donneesGraphe?.graphCurves || [])
        .map((c) => { try { return Number((c.formula || '').match(/\d+/g)?.[0]); } catch { return null; } })
        .filter(Boolean)
    )];
  }, [donneesGraphe?.id_sensor, donneesGraphe?.graphCurves, donnees]);

  const dateOptions = useMemo(() => {
    const tArr = donnees[0]?.x || donnees[0]?.t || [];
    const yArr = donnees[0]?.y || [];
    const pkArr = donnees[0]?.pk || [];
    return tArr
      .map((ts, i) => ({ ts, y: yArr[i], pk: pkArr[i] ?? i }))
      .filter((_, i) => i % 2 === 0);
  }, [donnees]);

  const {
    localLabels, loadingAnnotations,
    editingLabel, setEditingLabel,
    creatingLabel, setCreatingLabel,
    labelError, setLabelError,
    hoveredLabel, setHoveredLabel,
    timerEtiquette,
    addLabel, handleAddLabelFromSignal,
    handleSaveLabel, handleRemoveLabel, handlePencilEdit,
  } = useLabelCrud(labelGraphId, sensorIds, donnees, graphCurves, dateOptions);

  const {
    localPhases,
    editingPhase, setEditingPhase,
    creatingPhase, setCreatingPhase,
    ajouterPhase, handleSavePhase, handleDeletePhase, handlePhaseClickInternal,
  } = usePhaseCrud(labelGraphId, sensorIds, donnees, dateOptions);

  const { allTraces, annotations, layout, sectionsRef, tRawRef, labelsRawRef } =
    usePlotlyData(donnees, configuration, localLabels, localPhases, graphCurves);

  const {
    activeLayout,
    signalHover, setSignalHover,
    keepSignalOpen, scheduleSignalDismiss,
    handleHover, handleUnhover, handleClick, handleClickAnnotation,
  } = usePlotlyEvents({
    isLabelMode, layout, annotations,
    labelsRawRef, sectionsRef, tRawRef,
    setHoveredLabel, timerEtiquette,
    handlePhaseClickInternal,
    onLabelHover, onLabelUnhover, onPhaseClick,
  });

  const plotHeight =
    isLabelMode && dimensionHeight !== undefined
      ? Math.max(200, dimensionHeight - TOOLBAR_H)
      : dimensionHeight;

  const plotly = (
    <BibliothequePlotly
      data={allTraces}
      layout={activeLayout}
      donneesGraphe={donneesGraphe}
      dimensionWidth={dimensionWidth}
      dimensionHeight={plotHeight}
      viewobj_json_content={viewobj_json_content}
      onClick={handleClick}
      onHover={handleHover}
      onUnhover={handleUnhover}
      onClickAnnotation={handleClickAnnotation}
      ouvrirPopupModifierIntervalle={switchAfficherModifierIntervalle}
    />
  );

  // In label mode use the internal handler; otherwise forward the external prop.
  const addLabelFromSignalFn = isLabelMode ? handleAddLabelFromSignal : (onAddLabel || null);

  const signalPopup = (
    <SignalHoverPopup
      signalHover={signalHover}
      onMouseEnter={keepSignalOpen}
      onMouseLeave={scheduleSignalDismiss}
      onAddLabel={addLabelFromSignalFn}
      onClose={() => setSignalHover(null)}
    />
  );

  if (!isLabelMode) {
    return <>{plotly}{signalPopup}</>;
  }

  return (
    <div>
      <LabelToolbar
        loading={loadingAnnotations}
        onAddLabel={addLabel}
        onAddPhase={ajouterPhase}
      />

      {plotly}
      {signalPopup}

      {hoveredLabel && !editingLabel && (
        <LabelHoverCard
          labelData={hoveredLabel.label}
          x={hoveredLabel.x}
          y={hoveredLabel.y}
          onEdit={handlePencilEdit}
          onRemove={handleRemoveLabel}
          onMouseEnter={() => clearTimeout(timerEtiquette.current)}
          onMouseLeave={() => {
            timerEtiquette.current = setTimeout(() => setHoveredLabel(null), 300);
          }}
        />
      )}

      {editingLabel && (
        <LabelEditDialog
          label={editingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={donnees}
          error={labelError}
          onSave={handleSaveLabel}
          onCancel={() => { setEditingLabel(null); setLabelError(null); }}
        />
      )}

      {creatingLabel && (
        <LabelEditDialog
          label={creatingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={donnees}
          error={labelError}
          onSave={handleSaveLabel}
          onCancel={() => { setCreatingLabel(null); setLabelError(null); }}
        />
      )}

      {editingPhase && (
        <PhaseEditDialog
          phase={editingPhase}
          onSave={handleSavePhase}
          onCancel={() => setEditingPhase(null)}
          onDelete={handleDeletePhase}
        />
      )}

      {creatingPhase && (
        <PhaseEditDialog
          phase={creatingPhase}
          onSave={handleSavePhase}
          onCancel={() => setCreatingPhase(null)}
        />
      )}
    </div>
  );
};

export default LabelEventGraph;
